import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { Box } from 'Components/Base';
import { MATERIAL_TYPES_STRING, MATERIAL_TYPES } from 'Components/SubSection';

import ActionBar from '../Common/ActionBar';
import FormGenerator from '../../Form/FormGenerator';

import { modeOptions, toFormValue, toContent } from './settings';

const inputList = [
  {
    inputType: FormGenerator.INPUT_TYPE.TEXT_FIELD,
    inputLabel: 'Title',
    inputName: 'title',
    inputProps: {
      required: true,
    },
  },
  {
    inputType: FormGenerator.INPUT_TYPE.TEXT_FIELD,
    inputLabel: 'Description',
    inputName: 'description',
  },
  {
    inputType: FormGenerator.INPUT_TYPE.TEXT_FIELD,
    inputLabel: 'Scenario ID',
    inputName: 'scenarioId',
    inputProps: {
      required: true,
    },
  },
  {
    // A dropdown rather than a toggle: withFormsy seeds the model with
    // `if (defaultValue) setValue(...)`, so a boolean false never registers and the
    // field would start undefined.
    inputType: FormGenerator.INPUT_TYPE.DROPDOWN,
    inputLabel: 'Interaction mode',
    inputName: 'mode',
    inputProps: {
      required: true,
      options: modeOptions,
    },
  },
];

/**
 * The role play itself is a UMD module the classroom loads at runtime. This editor owns
 * the scenario to run plus the per-lesson settings that module reads — see ./settings.js
 * for why those travel untyped, and why saving merges over the stored content rather than
 * rebuilding it.
 */
const AIRolePlayEditor = ({
  title,
  description,
  content,
  materialTypeString,
  newAsset,
  loading,
  onCancel,
  onCreateMaterial,
}) => {
  const initialData = toFormValue({ title, description, content });
  const [formData, setFormData] = useState(initialData);

  useEffect(() => {
    setFormData(toFormValue({ title, description, content }));
    // `content` is a fresh object on every render of the parent query component, so the
    // fields it contributes are listed rather than the object itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, content.scenarioId, content.settings]);

  const handleFormChange = (value) => {
    setFormData(value);
  };

  const handleAction = () => {
    const { title: formTitle, description: formDescription } = formData;
    onCreateMaterial({
      title: formTitle,
      description: formDescription,
      // Merged over the stored content, never rebuilt from form state — that is what
      // keeps a setting this build does not render from being wiped on save.
      content: toContent(content, formData),
    });
  };

  return (
    <Box>
      <Box>
        <FormGenerator
          fields={inputList}
          showAction={false}
          initialData={initialData}
          onChange={handleFormChange}
        />
      </Box>
      <Box mt={2}>
        <ActionBar
          isCreate={newAsset}
          onAction={handleAction}
          materialTypeString={materialTypeString}
          onCancel={onCancel}
          loading={loading}
          disableSubmit={!formData.scenarioId}
        />
      </Box>
    </Box>
  );
};

AIRolePlayEditor.propTypes = {
  onCancel: PropTypes.func,
  newAsset: PropTypes.bool,
  materialTypeString: PropTypes.string,
  loading: PropTypes.bool,
  title: PropTypes.string,
  description: PropTypes.string,
  content: PropTypes.shape({
    scenarioId: PropTypes.string,
    settings: PropTypes.object,
  }),
  onCreateMaterial: PropTypes.func,
};

AIRolePlayEditor.defaultProps = {
  onCancel: () => {},
  materialTypeString: MATERIAL_TYPES_STRING[MATERIAL_TYPES.AI_ROLE_PLAY],
  title: '',
  description: '',
  content: {},
  onCreateMaterial: () => {},
};

export default AIRolePlayEditor;
